import type { AuthUser } from '@/features/auth/auth';
import { hasPermission } from '@/lib/auth/permissions';

/**
 * Who someone is, decided from `userType` — the authorization discriminator
 * stored on platform."User" and returned by `/auth/me`.
 *
 * These predicates answer *what kind of principal* this is. That is a
 * different question from `hasPermission`, which answers what a principal may
 * touch, and the two are not interchangeable: administrative access is a
 * property of the person, not a grant that can be handed out. The backend
 * draws the same line — `require_admin` in offerings-service, and
 * `ADMIN_ONLY_SCOPES` in shared, which refuses to store a configuration scope
 * on a non-admin however the matrix is edited.
 *
 * Every predicate fails closed on a null or partly-loaded user: the bare login
 * response carries no `userType`, and until `/auth/me` fills it in the answer
 * must be "no".
 */

/** Global platform operator. No tenant, bypasses every tenant-scoped check. */
export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
  return user?.userType === 'superuser';
}

/** Tenant administrator — the only role that reaches configuration. */
export function isTenantAdmin(user: AuthUser | null | undefined): boolean {
  return user?.userType === 'admin';
}

/**
 * Tenant-administrator tier: the tenant admin or the global superuser. The
 * ceiling for the most sensitive operations — financial write-offs, lock
 * overrides, administrative resets — and for every configuration surface.
 */
export function isTenantAdminOrAbove(
  user: AuthUser | null | undefined
): boolean {
  return isTenantAdmin(user) || isSuperAdmin(user);
}

/**
 * Whether the user may see and reach administrative configuration: users and
 * their access, tenant settings, policy rules, branches, the lock engine, the
 * notification templates and the audit trail.
 *
 * Same membership as {@link isTenantAdminOrAbove}, named for the menu rather
 * than the capability. Hiding the menu is only half of it — `route-access.ts`
 * blocks the URLs and the services refuse the calls.
 */
export function canAccessSystemSection(
  user: AuthUser | null | undefined
): boolean {
  return isTenantAdminOrAbove(user);
}

/**
 * How wide this operator's *lists* are. Single records open in full at any
 * tier — an agent must be able to serve a customer another agent signed up —
 * so this is about what a listing or a total includes, not about reachability.
 */
export function isBranchTier(user: AuthUser | null | undefined): boolean {
  return !isTenantAdminOrAbove(user) && user?.accessTier === 'branch';
}

/** Sees only their own book: the records they created. */
export function isSelfTier(user: AuthUser | null | undefined): boolean {
  return !isTenantAdminOrAbove(user) && user?.accessTier === 'self';
}

/**
 * Manager tier and up: anyone whose lists reach past their own book. The floor
 * for supervised operational actions; a self-tier agent is excluded.
 */
export function isManagerOrAbove(user: AuthUser | null | undefined): boolean {
  return isTenantAdminOrAbove(user) || isBranchTier(user);
}

/**
 * Device-lifecycle actions an operator can trigger manually from the portal
 * (the lifecycle is otherwise policy-automated). Each is a first-class,
 * individually grantable permission on the `device_units` scope — mirroring
 * the canShare pattern — so authorisation is driven by the user's permission
 * grants, not by a hard-coded role name.
 *
 * The literal values match the device-action keys in `ACTION_KEY`
 * (`@/lib/auth/permissions`), so an action can be passed straight to
 * {@link hasPermission}. The role templates decide the defaults: an agent gets
 * the field verbs (lock, unlock, sync), a branch manager adds the supervised
 * ones, and write-off, remove-lock and reset-state stay with the tenant admin.
 */
export type DeviceLifecycleAction =
  | 'transfer'
  | 'updateExpiration'
  | 'issueOfflinePin'
  | 'syncState'
  | 'resetState'
  | 'processReturn'
  | 'writeOff'
  | 'lock'
  | 'unlock'
  | 'removeLock';

/**
 * Whether `user` may perform the given device lifecycle action. Resolved
 * against the `device_units` permission grants (superusers bypass); the
 * backend enforces the same check per endpoint.
 */
export function canPerformDeviceAction(
  user: AuthUser | null | undefined,
  action: DeviceLifecycleAction
): boolean {
  return hasPermission(user, 'device_units', action);
}
